import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '@components/ui/feedback';
import { useOrganizationInfo, useSubscriptionDetails,useUserRoles } from '@features/user';
import { hasBillingAccess } from '@utils/roleUtils';

import { EnterpriseTrialEndedView } from './components/EnterpriseTrialEndedView';
import { SubscriptionMultiStepFlow } from './components/SubscriptionMultiStepFlow';

interface SubscriptionFlowContainerProps {
  user_email?: string;
  payment_status?: string;
}

export const SubscriptionFlowContainer: React.FC<SubscriptionFlowContainerProps> = () => {
  const orgInfo = useOrganizationInfo();
  const userRoles = useUserRoles();
  const isPracticeAdmin = hasBillingAccess(userRoles);
  const navigate = useNavigate();
  const { data: subscriptionDetails } = useSubscriptionDetails();

  const isGroup = orgInfo?.group ?? false;

  // Calculate trial end date from subscription details
  const trialEndDate = useMemo(() => {
    if (!subscriptionDetails?.trial_end) {
      return undefined;
    }
    // Convert UNIX timestamp (seconds) to Date (milliseconds)
    return new Date(subscriptionDetails.trial_end * 1000);
  }, [subscriptionDetails?.trial_end]);

  // Redirect practice administrators in group organizations to clinicians page
  useEffect(() => {
    if (orgInfo !== undefined && isGroup && isPracticeAdmin) {
      navigate('/practice/clinicians');
    }
  }, [orgInfo, isGroup, isPracticeAdmin, navigate]);

  // Show loading state while organization info is being fetched
  if (orgInfo === undefined) {
    return <CenteredSpinner />;
  }

  // Show loading state while navigating (practice admin in group org)
  if (isGroup && isPracticeAdmin) {
    return <CenteredSpinner />;
  }

  // Fork based on whether this is a group organization
  if (!isGroup) {
    // Individual or small practice: Multi-step subscription flow
    return <SubscriptionMultiStepFlow trialEndDate={trialEndDate} />;
  } else {
    // Group organization non-admin: Contact administrator message
    return <EnterpriseTrialEndedView />;
  }
};
